import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { quizAPI, courseAPI, categoryAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  ClipboardList,
  Plus,
  Trash2,
  GripVertical,
  CheckCircle,
  Circle,
  BookOpen,
  Layers,
  Code,
  Eye,
  EyeOff,
  FileCode
} from "lucide-react";
import { toast } from "sonner";

// Quiz HTML Templates
const QUIZ_TEMPLATE_BASIC = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
    .question-card { margin-bottom: 1.5rem; }
    .option-label { cursor: pointer; padding: 0.75rem 1rem; border: 2px solid #e2e8f0; border-radius: 0.5rem; display: block; margin-bottom: 0.5rem; transition: all 0.2s; }
    .option-label:hover { border-color: #6366f1; background: #f8fafc; }
    input[type="radio"]:checked + .option-label { border-color: #6366f1; background: #eef2ff; }
    .btn-submit { background: #6366f1; border: none; padding: 0.75rem 2rem; }
    .btn-submit:hover { background: #4f46e5; }
    .result-box { padding: 1.5rem; border-radius: 0.75rem; text-align: center; }
    .result-success { background: #dcfce7; color: #166534; }
    .result-warning { background: #fef3c7; color: #92400e; }
    .result-error { background: #fee2e2; color: #991b1b; }
  </style>
</head>
<body>
  <div class="container py-4">
    <form id="quizForm">
      <div class="question-card">
        <h5>Question 1: Quelle commande permet de lister les conteneurs Docker ?</h5>
        <input type="radio" name="q1" value="a" id="q1a" style="display:none">
        <label for="q1a" class="option-label">A) docker list</label>
        <input type="radio" name="q1" value="b" id="q1b" style="display:none">
        <label for="q1b" class="option-label">B) docker ps</label>
        <input type="radio" name="q1" value="c" id="q1c" style="display:none">
        <label for="q1c" class="option-label">C) docker show</label>
      </div>
      
      <div class="question-card">
        <h5>Question 2: Quel fichier définit les services Docker Compose ?</h5>
        <input type="radio" name="q2" value="a" id="q2a" style="display:none">
        <label for="q2a" class="option-label">A) Dockerfile</label>
        <input type="radio" name="q2" value="b" id="q2b" style="display:none">
        <label for="q2b" class="option-label">B) docker-compose.yml</label>
        <input type="radio" name="q2" value="c" id="q2c" style="display:none">
        <label for="q2c" class="option-label">C) config.json</label>
      </div>
      
      <button type="submit" class="btn btn-submit btn-lg text-white">Valider mes réponses</button>
    </form>
    
    <div id="result" class="result-box mt-4" style="display:none"></div>
  </div>
  
  <script>
    const answers = { q1: 'b', q2: 'b' };
    
    document.getElementById('quizForm').addEventListener('submit', function(e) {
      e.preventDefault();
      let score = 0;
      const total = Object.keys(answers).length;
      
      for (const [q, correct] of Object.entries(answers)) {
        const selected = document.querySelector('input[name="'+q+'"]:checked');
        if (selected && selected.value === correct) score++;
      }
      
      const percent = Math.round((score / total) * 100);
      const resultDiv = document.getElementById('result');
      resultDiv.style.display = 'block';
      
      if (percent >= 80) {
        resultDiv.className = 'result-box mt-4 result-success';
        resultDiv.innerHTML = '<h3>Excellent ! 🎉</h3><p>Score: ' + score + '/' + total + ' (' + percent + '%)</p>';
      } else if (percent >= 50) {
        resultDiv.className = 'result-box mt-4 result-warning';
        resultDiv.innerHTML = '<h3>Pas mal ! 👍</h3><p>Score: ' + score + '/' + total + ' (' + percent + '%)</p>';
      } else {
        resultDiv.className = 'result-box mt-4 result-error';
        resultDiv.innerHTML = '<h3>À revoir 📚</h3><p>Score: ' + score + '/' + total + ' (' + percent + '%)</p>';
      }
    });
  </script>
</body>
</html>`;

const QUIZ_TEMPLATE_INTERACTIVE = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet">
  <style>
    body { font-family: system-ui, sans-serif; background: #f8fafc; }
    .quiz-container { max-width: 600px; margin: 0 auto; }
    .question-box { background: white; padding: 2rem; border-radius: 1rem; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); }
    .option-btn { width: 100%; padding: 1rem; margin: 0.5rem 0; border: 2px solid #e2e8f0; border-radius: 0.75rem; background: white; cursor: pointer; transition: all 0.3s; text-align: left; }
    .option-btn:hover { border-color: #6366f1; transform: translateX(4px); }
    .option-btn.correct { background: #dcfce7; border-color: #22c55e; }
    .option-btn.incorrect { background: #fee2e2; border-color: #ef4444; }
    .progress-bar { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
    .progress-fill { height: 100%; background: linear-gradient(90deg, #6366f1, #8b5cf6); transition: width 0.5s; }
    .score-display { font-size: 3rem; font-weight: bold; color: #6366f1; }
  </style>
</head>
<body>
  <div class="container py-4">
    <div class="quiz-container">
      <div class="progress-bar mb-4">
        <div class="progress-fill" id="progress" style="width: 0%"></div>
      </div>
      <div id="questionBox" class="question-box"></div>
    </div>
  </div>
  <script>
    const questions = [
      { q: "Qu'est-ce que Docker ?", options: ["Un langage de programmation", "Une plateforme de conteneurisation", "Un système d'exploitation", "Un navigateur web"], correct: 1 },
      { q: "Quelle commande construit une image Docker ?", options: ["docker run", "docker build", "docker create", "docker start"], correct: 1 },
      { q: "Où sont stockées les images Docker par défaut ?", options: ["GitHub", "Docker Hub", "GitLab", "AWS"], correct: 1 }
    ];
    let current = 0, score = 0;
    
    function showQuestion() {
      if (current >= questions.length) {
        const percent = Math.round((score / questions.length) * 100);
        document.getElementById('questionBox').innerHTML = 
          '<div class="text-center"><div class="score-display">' + score + '/' + questions.length + '</div>' +
          '<p class="text-muted">Score: ' + percent + '%</p>' +
          '<button class="btn btn-primary mt-3" onclick="restart()">Recommencer</button></div>';
        return;
      }
      const q = questions[current];
      document.getElementById('progress').style.width = ((current / questions.length) * 100) + '%';
      let html = '<h4 class="mb-4">Question ' + (current + 1) + '/' + questions.length + '</h4>';
      html += '<p class="lead mb-4">' + q.q + '</p>';
      q.options.forEach((opt, i) => {
        html += '<button class="option-btn" onclick="answer(' + i + ')">' + opt + '</button>';
      });
      document.getElementById('questionBox').innerHTML = html;
    }
    
    function answer(selected) {
      const btns = document.querySelectorAll('.option-btn');
      btns.forEach((btn, i) => {
        btn.disabled = true;
        if (i === questions[current].correct) btn.classList.add('correct');
        else if (i === selected) btn.classList.add('incorrect');
      });
      if (selected === questions[current].correct) score++;
      setTimeout(() => { current++; showQuestion(); }, 1000);
    }
    
    function restart() { current = 0; score = 0; showQuestion(); }
    showQuestion();
  </script>
</body>
</html>`;

const QUIZ_TEMPLATE_TIMER = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet">
  <style>
    body { font-family: system-ui, sans-serif; }
    .timer { font-size: 2rem; font-weight: bold; color: #6366f1; }
    .timer.warning { color: #f59e0b; }
    .timer.danger { color: #ef4444; animation: pulse 1s infinite; }
    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    .question-card { background: white; padding: 1.5rem; border-radius: 0.75rem; border: 1px solid #e2e8f0; margin-bottom: 1rem; }
  </style>
</head>
<body class="bg-light">
  <div class="container py-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4>Quiz Chronométré</h4>
      <div class="timer" id="timer">05:00</div>
    </div>
    <form id="quizForm">
      <div class="question-card">
        <h6>1. Quelle est la capitale de la France ?</h6>
        <div class="form-check"><input class="form-check-input" type="radio" name="q1" value="a"><label class="form-check-label">Lyon</label></div>
        <div class="form-check"><input class="form-check-input" type="radio" name="q1" value="b"><label class="form-check-label">Paris</label></div>
        <div class="form-check"><input class="form-check-input" type="radio" name="q1" value="c"><label class="form-check-label">Marseille</label></div>
      </div>
      <div class="question-card">
        <h6>2. Combien font 2 + 2 ?</h6>
        <div class="form-check"><input class="form-check-input" type="radio" name="q2" value="a"><label class="form-check-label">3</label></div>
        <div class="form-check"><input class="form-check-input" type="radio" name="q2" value="b"><label class="form-check-label">4</label></div>
        <div class="form-check"><input class="form-check-input" type="radio" name="q2" value="c"><label class="form-check-label">5</label></div>
      </div>
      <button type="submit" class="btn btn-primary btn-lg">Soumettre</button>
    </form>
    <div id="result" class="alert mt-4" style="display:none"></div>
  </div>
  <script>
    let timeLeft = 300; // 5 minutes
    const answers = { q1: 'b', q2: 'b' };
    
    const timerInterval = setInterval(() => {
      timeLeft--;
      const min = Math.floor(timeLeft / 60).toString().padStart(2, '0');
      const sec = (timeLeft % 60).toString().padStart(2, '0');
      const timerEl = document.getElementById('timer');
      timerEl.textContent = min + ':' + sec;
      if (timeLeft <= 60) timerEl.className = 'timer danger';
      else if (timeLeft <= 120) timerEl.className = 'timer warning';
      if (timeLeft <= 0) { clearInterval(timerInterval); document.getElementById('quizForm').dispatchEvent(new Event('submit')); }
    }, 1000);
    
    document.getElementById('quizForm').addEventListener('submit', function(e) {
      e.preventDefault();
      clearInterval(timerInterval);
      let score = 0;
      for (const [q, correct] of Object.entries(answers)) {
        const sel = document.querySelector('input[name="'+q+'"]:checked');
        if (sel && sel.value === correct) score++;
      }
      const resultDiv = document.getElementById('result');
      resultDiv.style.display = 'block';
      resultDiv.className = 'alert mt-4 ' + (score === Object.keys(answers).length ? 'alert-success' : 'alert-warning');
      resultDiv.innerHTML = '<strong>Résultat:</strong> ' + score + '/' + Object.keys(answers).length + ' bonnes réponses';
    });
  </script>
</body>
</html>`;

const QUESTION_TYPES = [
  { value: "qcm", label: "QCM (une seule réponse)" },
  { value: "multiple", label: "Choix multiples (plusieurs réponses)" },
  { value: "true_false", label: "Vrai / Faux" },
  { value: "short_answer", label: "Réponse courte" },
];

const emptyQuestion = {
  id: "",
  question_type: "qcm",
  question_text: "",
  options: ["", ""],
  correct_answers: [],
  points: 1,
  explanation: "",
};

export default function QuizEditorPage() {
  const { workspaceId, quizId } = useParams();
  const navigate = useNavigate();
  const isNew = !quizId || quizId === "new";
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [htmlDialogOpen, setHtmlDialogOpen] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    chapter: "",
    category_id: null,
    time_limit_minutes: 30,
    passing_score: 60,
    shuffle_questions: false,
    show_results: true,
    questions: [],
    is_visible: true,
    course_id: null,
    section_id: null,
    custom_html: "",
    custom_html_height: 600,
  });

  useEffect(() => {
    loadCourses();
    categoryAPI.list(workspaceId).then(res => setCategoriesList(res.data)).catch(() => {});
    if (!isNew && quizId) {
      setLoading(true);
      loadQuiz();
    }
  }, [quizId, isNew]);

  const loadCourses = async () => {
    try {
      const response = await courseAPI.list(workspaceId);
      setCourses(response.data);
    } catch (error) {
      console.error("Failed to load courses:", error);
    }
  };

  const loadQuiz = async () => {
    try {
      const response = await quizAPI.get(workspaceId, quizId);
      setFormData({
        title: response.data.title || "",
        description: response.data.description || "",
        chapter: response.data.chapter || "",
        category_id: response.data.category_id || null,
        time_limit_minutes: response.data.time_limit_minutes || 30,
        passing_score: response.data.passing_score || 60,
        shuffle_questions: response.data.shuffle_questions || false,
        show_results: response.data.show_results !== false,
        questions: response.data.questions || [],
        is_visible: response.data.is_visible !== false,
        course_id: response.data.course_id || null,
        section_id: response.data.section_id || null,
        custom_html: response.data.custom_html || "",
        custom_html_height: response.data.custom_html_height || 600,
      });
    } catch (error) {
      console.error("Failed to load quiz:", error);
      toast.error("Erreur lors du chargement du quiz");
      navigate(`/workspace/${workspaceId}/quiz`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }

    // If no custom HTML, require at least one question
    if (!formData.custom_html && formData.questions.length === 0) {
      toast.error("Ajoutez au moins une question ou importez un Quiz HTML");
      return;
    }

    // Validate questions if present
    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      if (!q.question_text.trim()) {
        toast.error(`Question ${i + 1}: Le texte de la question est requis`);
        return;
      }
      if (q.correct_answers.length === 0) {
        toast.error(`Question ${i + 1}: Sélectionnez au moins une réponse correcte`);
        return;
      }
    }

    setSaving(true);
    try {
      if (isNew) {
        await quizAPI.create(workspaceId, formData);
        toast.success("Quiz créé avec succès");
      } else {
        await quizAPI.update(workspaceId, quizId, formData);
        toast.success("Quiz mis à jour");
      }
      navigate(`/workspace/${workspaceId}/quiz`);
    } catch (error) {
      console.error("Failed to save quiz:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [...formData.questions, { ...emptyQuestion, id: `q_${Date.now()}` }]
    });
  };

  const removeQuestion = (index) => {
    const newQuestions = [...formData.questions];
    newQuestions.splice(index, 1);
    setFormData({ ...formData, questions: newQuestions });
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    
    // Reset options and correct_answers when changing question type
    if (field === "question_type") {
      if (value === "true_false") {
        newQuestions[index].options = ["Vrai", "Faux"];
        newQuestions[index].correct_answers = [];
      } else if (value === "short_answer") {
        newQuestions[index].options = [];
        newQuestions[index].correct_answers = [""];
      } else {
        newQuestions[index].options = ["", ""];
        newQuestions[index].correct_answers = [];
      }
    }
    
    setFormData({ ...formData, questions: newQuestions });
  };

  const addOption = (qIndex) => {
    const newQuestions = [...formData.questions];
    newQuestions[qIndex].options.push("");
    setFormData({ ...formData, questions: newQuestions });
  };

  const removeOption = (qIndex, optIndex) => {
    const newQuestions = [...formData.questions];
    const removedOption = newQuestions[qIndex].options[optIndex];
    newQuestions[qIndex].options.splice(optIndex, 1);
    // Remove from correct answers if was selected
    newQuestions[qIndex].correct_answers = newQuestions[qIndex].correct_answers.filter(a => a !== removedOption);
    setFormData({ ...formData, questions: newQuestions });
  };

  const updateOption = (qIndex, optIndex, value) => {
    const newQuestions = [...formData.questions];
    const oldValue = newQuestions[qIndex].options[optIndex];
    newQuestions[qIndex].options[optIndex] = value;
    // Update correct_answers if this option was selected
    if (newQuestions[qIndex].correct_answers.includes(oldValue)) {
      newQuestions[qIndex].correct_answers = newQuestions[qIndex].correct_answers.map(a => a === oldValue ? value : a);
    }
    setFormData({ ...formData, questions: newQuestions });
  };

  const toggleCorrectAnswer = (qIndex, option) => {
    const newQuestions = [...formData.questions];
    const q = newQuestions[qIndex];
    
    if (q.question_type === "qcm" || q.question_type === "true_false") {
      // Single answer
      q.correct_answers = [option];
    } else if (q.question_type === "multiple") {
      // Multiple answers
      if (q.correct_answers.includes(option)) {
        q.correct_answers = q.correct_answers.filter(a => a !== option);
      } else {
        q.correct_answers.push(option);
      }
    }
    
    setFormData({ ...formData, questions: newQuestions });
  };

  const updateShortAnswer = (qIndex, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[qIndex].correct_answers = [value];
    setFormData({ ...formData, questions: newQuestions });
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/workspace/${workspaceId}/quiz`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {isNew ? "Nouveau Quiz" : "Modifier le Quiz"}
            </h1>
            <p className="text-muted-foreground">
              {formData.questions.length} question(s)
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content - Questions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quiz Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-purple-600" />
                  Informations générales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Quiz Docker - Les bases"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description du quiz..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Custom HTML Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileCode className="h-5 w-5 text-blue-600" />
                    Quiz HTML personnalisé
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {formData.custom_html && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                        className="gap-2"
                      >
                        {showHtmlPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        {showHtmlPreview ? "Masquer" : "Afficher"}
                      </Button>
                    )}
                    <Dialog open={htmlDialogOpen} onOpenChange={setHtmlDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Code className="h-4 w-4" />
                          {formData.custom_html ? "Modifier le HTML" : "Importer HTML"}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
                        <DialogHeader>
                          <DialogTitle>Quiz HTML personnalisé</DialogTitle>
                        </DialogHeader>
                        <Tabs defaultValue="code" className="flex-1 flex flex-col overflow-hidden">
                          <TabsList>
                            <TabsTrigger value="code">Code HTML</TabsTrigger>
                            <TabsTrigger value="templates">Templates Quiz</TabsTrigger>
                          </TabsList>
                          <TabsContent value="code" className="flex-1 space-y-4 overflow-auto">
                            <div className="space-y-2">
                              <Label>Code HTML (supporte les pages complètes avec CSS/Bootstrap/JavaScript)</Label>
                              <Textarea
                                placeholder={`<!DOCTYPE html>
<html>
<head>
  <link href='https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css' rel='stylesheet'>
  <style>
    /* Vos styles CSS */
  </style>
</head>
<body>
  <div class="container py-4">
    <h1>Mon Quiz Personnalisé</h1>
    <!-- Questions du quiz -->
  </div>
  <script>
    // Votre logique JavaScript
  </script>
</body>
</html>`}
                                value={formData.custom_html}
                                onChange={(e) => setFormData({ ...formData, custom_html: e.target.value })}
                                rows={15}
                                className="font-mono text-sm"
                              />
                              <p className="text-xs text-muted-foreground">
                                Vous pouvez coller un quiz HTML complet avec Bootstrap, CSS personnalisé et JavaScript. 
                                Il sera rendu dans une iframe isolée.
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label>Hauteur de l'iframe (px)</Label>
                              <Input
                                type="number"
                                value={formData.custom_html_height}
                                onChange={(e) => setFormData({ ...formData, custom_html_height: parseInt(e.target.value) || 600 })}
                                placeholder="600"
                              />
                            </div>
                          </TabsContent>
                          <TabsContent value="templates" className="space-y-2 overflow-auto p-2">
                            <Button 
                              variant="outline" 
                              className="w-full justify-start text-left h-auto py-3" 
                              onClick={() => setFormData({ ...formData, custom_html: QUIZ_TEMPLATE_BASIC })}
                            >
                              <div>
                                <div className="font-medium">📝 Quiz basique avec score</div>
                                <div className="text-xs text-muted-foreground">QCM simple avec calcul automatique du score</div>
                              </div>
                            </Button>
                            <Button 
                              variant="outline" 
                              className="w-full justify-start text-left h-auto py-3" 
                              onClick={() => setFormData({ ...formData, custom_html: QUIZ_TEMPLATE_INTERACTIVE })}
                            >
                              <div>
                                <div className="font-medium">🎯 Quiz interactif animé</div>
                                <div className="text-xs text-muted-foreground">Questions avec feedback immédiat et animations</div>
                              </div>
                            </Button>
                            <Button 
                              variant="outline" 
                              className="w-full justify-start text-left h-auto py-3" 
                              onClick={() => setFormData({ ...formData, custom_html: QUIZ_TEMPLATE_TIMER })}
                            >
                              <div>
                                <div className="font-medium">⏱️ Quiz chronométré</div>
                                <div className="text-xs text-muted-foreground">Quiz avec timer et limite de temps</div>
                              </div>
                            </Button>
                          </TabsContent>
                        </Tabs>
                        <div className="flex justify-between gap-2 mt-4 pt-4 border-t">
                          {formData.custom_html && (
                            <Button 
                              variant="destructive" 
                              onClick={() => {
                                setFormData({ ...formData, custom_html: "" });
                                setHtmlDialogOpen(false);
                              }}
                            >
                              Supprimer le HTML
                            </Button>
                          )}
                          <div className="flex gap-2 ml-auto">
                            <Button variant="outline" onClick={() => setHtmlDialogOpen(false)}>Annuler</Button>
                            <Button onClick={() => {
                              setHtmlDialogOpen(false);
                              if (formData.custom_html) {
                                toast.success("Quiz HTML mis à jour");
                              }
                            }}>
                              Appliquer
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {formData.custom_html ? (
                  <>
                    {showHtmlPreview && (
                      <div className="border rounded-lg overflow-hidden">
                        <iframe
                          srcDoc={formData.custom_html}
                          className="w-full border-0"
                          style={{ height: `${formData.custom_html_height}px` }}
                          title="Quiz HTML Preview"
                          sandbox="allow-scripts allow-same-origin"
                        />
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground mt-2">
                      Ce quiz utilise un rendu HTML personnalisé. Vous pouvez également ajouter des questions manuelles ci-dessous.
                    </p>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileCode className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>Aucun HTML personnalisé</p>
                    <p className="text-sm">Cliquez sur "Importer HTML" pour ajouter un quiz personnalisé</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Questions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Questions</h2>
                <Button onClick={addQuestion} variant="outline" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Ajouter une question
                </Button>
              </div>

              {formData.questions.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucune question pour le moment</p>
                    <Button onClick={addQuestion} className="mt-4 gap-2">
                      <Plus className="h-4 w-4" />
                      Ajouter une question
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                formData.questions.map((question, qIndex) => (
                  <Card key={question.id || qIndex} className="relative">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                          <Badge variant="outline">Question {qIndex + 1}</Badge>
                          <Badge>{QUESTION_TYPES.find(t => t.value === question.question_type)?.label}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeQuestion(qIndex)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Question Type */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Type de question</Label>
                          <Select
                            value={question.question_type}
                            onValueChange={(value) => updateQuestion(qIndex, "question_type", value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {QUESTION_TYPES.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Points</Label>
                          <Input
                            type="number"
                            min="1"
                            value={question.points}
                            onChange={(e) => updateQuestion(qIndex, "points", parseInt(e.target.value) || 1)}
                          />
                        </div>
                      </div>

                      {/* Question Text */}
                      <div className="space-y-2">
                        <Label>Question *</Label>
                        <Textarea
                          value={question.question_text}
                          onChange={(e) => updateQuestion(qIndex, "question_text", e.target.value)}
                          placeholder="Écrivez votre question ici..."
                          rows={2}
                        />
                      </div>

                      {/* Options for QCM, Multiple, True/False */}
                      {(question.question_type === "qcm" || question.question_type === "multiple" || question.question_type === "true_false") && (
                        <div className="space-y-2">
                          <Label>
                            Options {question.question_type === "multiple" && "(cliquez sur plusieurs pour sélectionner les bonnes réponses)"}
                          </Label>
                          <div className="space-y-2">
                            {question.options.map((option, optIndex) => (
                              <div key={optIndex} className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => toggleCorrectAnswer(qIndex, option)}
                                  className={`flex items-center justify-center h-8 w-8 rounded-full border-2 transition-colors ${
                                    question.correct_answers.includes(option)
                                      ? 'border-green-500 bg-green-50 text-green-600'
                                      : 'border-gray-300 hover:border-gray-400'
                                  }`}
                                >
                                  {question.correct_answers.includes(option) ? (
                                    <CheckCircle className="h-5 w-5" />
                                  ) : (
                                    <Circle className="h-5 w-5" />
                                  )}
                                </button>
                                <Input
                                  value={option}
                                  onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                                  placeholder={`Option ${optIndex + 1}`}
                                  disabled={question.question_type === "true_false"}
                                  className="flex-1"
                                />
                                {question.question_type !== "true_false" && question.options.length > 2 && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeOption(qIndex, optIndex)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            {question.question_type !== "true_false" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => addOption(qIndex)}
                                className="gap-1 mt-2"
                              >
                                <Plus className="h-3 w-3" />
                                Ajouter une option
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Short Answer */}
                      {question.question_type === "short_answer" && (
                        <div className="space-y-2">
                          <Label>Réponse attendue *</Label>
                          <Input
                            value={question.correct_answers[0] || ""}
                            onChange={(e) => updateShortAnswer(qIndex, e.target.value)}
                            placeholder="La réponse correcte..."
                          />
                          <p className="text-xs text-muted-foreground">
                            La réponse de l'utilisateur sera comparée sans tenir compte des majuscules/minuscules
                          </p>
                        </div>
                      )}

                      {/* Explanation */}
                      <div className="space-y-2">
                        <Label>Explication (optionnel)</Label>
                        <Textarea
                          value={question.explanation || ""}
                          onChange={(e) => updateQuestion(qIndex, "explanation", e.target.value)}
                          placeholder="Explication affichée après la réponse..."
                          rows={2}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Attachment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Rattachement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Rattacher à un cours</Label>
                  <Select
                    value={formData.course_id || "none"}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      course_id: value === "none" ? null : value,
                      section_id: value === "none" ? null : formData.section_id
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Aucun cours" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun cours</SelectItem>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.course_id && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Rattacher à une section
                    </Label>
                    <Select
                      value={formData.section_id || "none"}
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        section_id: value === "none" ? null : value 
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Aucune section" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Directement au cours</SelectItem>
                        {courses
                          .find(c => c.id === formData.course_id)
                          ?.sections?.map((section, index) => (
                            <SelectItem key={section.id} value={section.id}>
                              Section {index + 1}: {section.title}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Paramètres</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="chapter">Chapitre</Label>
                  <Input
                    id="chapter"
                    value={formData.chapter}
                    onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                    placeholder="Ex: Module 1"
                  />
                </div>
                {categoriesList.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="quiz-category">Catégorie</Label>
                    <select
                      id="quiz-category"
                      value={formData.category_id || ""}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="quiz-category-select"
                    >
                      <option value="">Sans catégorie</option>
                      {categoriesList.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="time_limit">Durée limite (minutes)</Label>
                  <Input
                    id="time_limit"
                    type="number"
                    min="1"
                    value={formData.time_limit_minutes}
                    onChange={(e) => setFormData({ ...formData, time_limit_minutes: parseInt(e.target.value) || 30 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="passing_score">Score minimum pour réussir (%)</Label>
                  <Input
                    id="passing_score"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.passing_score}
                    onChange={(e) => setFormData({ ...formData, passing_score: parseInt(e.target.value) || 60 })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Options */}
            <Card>
              <CardHeader>
                <CardTitle>Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Mélanger les questions</Label>
                    <p className="text-xs text-muted-foreground">
                      L'ordre des questions sera aléatoire
                    </p>
                  </div>
                  <Switch
                    checked={formData.shuffle_questions}
                    onCheckedChange={(checked) => setFormData({ ...formData, shuffle_questions: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Afficher les résultats</Label>
                    <p className="text-xs text-muted-foreground">
                      Montrer les bonnes réponses après
                    </p>
                  </div>
                  <Switch
                    checked={formData.show_results}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_results: checked })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Visibility */}
            <Card>
              <CardHeader>
                <CardTitle>Visibilité</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Visible pour les candidats</Label>
                    <p className="text-sm text-muted-foreground">
                      {formData.is_visible 
                        ? "Les candidats peuvent voir ce quiz" 
                        : "Ce quiz est caché aux candidats"}
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_visible: checked })}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
